import React, { useState, useEffect, useRef } from 'react';
import styles from './CompanySuggestionsPopup.module.css';
import { GCC_STUDIO_API_BASE_URL } from '../../runtimeConfig';
import { useAlphaSenseToken } from '../../context/AlphaSenseTokenContext';
import { useAuth } from '../../hooks/useAuth';
import { runWorkflowAgent, deepResearch } from '../../services/alphasenseService';

const CompanySuggestionsPopup = ({ isOpen, onClose, agent: agentProp }) => {
  const { alphasenseToken, oboToken, agents } = useAlphaSenseToken();
  const { getApiToken } = useAuth();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [running, setRunning] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const countdownRef = useRef(null);
  const runningRef = useRef(false);
  const retryCountdownRef = useRef(0);
  useEffect(() => { runningRef.current = running; }, [running]);
  useEffect(() => { retryCountdownRef.current = retryCountdown; }, [retryCountdown]);
  useEffect(() => () => clearInterval(countdownRef.current), []);

  const startCountdown = (seconds) => {
    setRetryCountdown(seconds);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setRetryCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownRef.current); setError(null); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const resetForm = () => {
    setQuery('');
    setSuggestions([]);
    setError(null);
    setSelectedCompany(null);
  };

  // Focus input when opened; reset stale state (e.g. an error from a request that
  // finished in the background while the popup was closed) unless a request is
  // still running or a retry countdown is actively waiting.
  useEffect(() => {
    if (isOpen) {
      if (!runningRef.current) {
        if (retryCountdownRef.current === 0) resetForm();
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    } else if (!runningRef.current && retryCountdownRef.current === 0) {
      resetForm();
    }
  }, [isOpen]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Debounced fetch on query change — reset selection when user types again
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (selectedCompany && query === selectedCompany.name) return;
    setSelectedCompany(null);
    if (!query.trim()) {
      setSuggestions([]);
      setError(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (!alphasenseToken) {
        setError('AlphaSense token not ready — please wait a moment.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${GCC_STUDIO_API_BASE_URL}/utility/companies/suggestions?query=${encodeURIComponent(query.trim())}&limit=15`,
          { headers: { Authorization: `Bearer ${alphasenseToken}` } }
        );
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : (data.suggestions ?? data.results ?? []));
      } catch (err) {
        setError('Failed to fetch suggestions. Please try again.');
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query, alphasenseToken, selectedCompany]);

  const handleCompanyClick = (item) => {
    const name = typeof item === 'string' ? item : (item.name ?? item.company_name ?? item.label ?? '');
    const ticker = typeof item === 'object' ? (item.primaryTickerCode || item.ticker || item.symbol || '') : '';
    setSelectedCompany({ raw: item, name, ticker });
    setQuery(name);
    setSuggestions([]);
    setError(null);
  };

  const handleGenerateReport = async () => {
    if (running || !selectedCompany) return;
    const agentId = agentProp?.id ?? agentProp?.agentId ?? agents?.[0]?.id ?? agents?.[0]?.agentId;
    if (!agentId) return;
    if (!oboToken) {
      setError('Session not ready — please wait a moment and try again.');
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const msalToken = await getApiToken();
      const { conversationId } = await runWorkflowAgent(agentId, msalToken, oboToken);
      await deepResearch(selectedCompany.name, conversationId, msalToken, oboToken);
      // Reset before closing so next open starts fresh
      resetForm();
      onClose();
    } catch (err) {
      if (err.code === 'CREDIT_LIMIT_EXHAUSTED') {
        setError(err.message);
        if (err.retryAfterSeconds) startCountdown(err.retryAfterSeconds);
      } else if (err?.status === 403) {
        setError('You are not authorized to access AlphaSense');
      } else if (err?.status === 401) {
        setError('Your AlphaSense connection has expired or is invalid. Please refresh the page and try again.');
      } else {
        setError('Failed to generate report. Please try again.');
      }
    } finally {
      setRunning(false);
    }
  };

  const runningStatusMessage = 'You can continue using the application while we generate your report. It will be ready in approximately 10 minutes and sent to you via email.';

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="suggestions-popup-title"
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title} id="suggestions-popup-title">Search Company</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className={styles.subtitle}>Conduct a company deep drive.</p>

        <div className={styles.inputWrap}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a company name or ticker…"
            aria-label="Company search query"
            autoComplete="off"
          />
          {loading && <div className={styles.spinner} aria-label="Loading" />}
        </div>

        {error && (
          <p className={styles.error}>
            {retryCountdown > 0 ? `Credit limit exceeded — retry in ${retryCountdown}s…` : error}
          </p>
        )}

        {suggestions.length > 0 && !selectedCompany && (
          <ul className={styles.list} role="listbox">
            <li className={styles.listGroupLabel}>COMPANIES</li>
            {suggestions.map((item, i) => {
              const name = typeof item === 'string' ? item : (item.name ?? item.company_name ?? item.label ?? '');
              const ticker = typeof item === 'object' ? (item.primaryTickerCode || item.ticker || item.symbol || '') : '';
              return (
                <li
                  key={item.id ?? i}
                  className={styles.listItem}
                  role="option"
                  onClick={() => handleCompanyClick(item)}
                >
                  {ticker ? (
                    <span className={styles.tickerBadge}>{ticker}</span>
                  ) : (
                    <span className={styles.tickerBadgeEmpty} />
                  )}
                  <span className={styles.itemName}>{name}</span>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && !error && query.trim() && suggestions.length === 0 && !selectedCompany && (
          <p className={styles.empty}>No suggestions found for "{query}".</p>
        )}

        {selectedCompany && (
          <div className={styles.selectedWrap}>
            <button
              type="button"
              className={styles.generateBtn}
              onClick={handleGenerateReport}
              disabled={running || retryCountdown > 0}
            >
              {running ? (
                <>
                  <span className={styles.btnSpinner} />
                  Generating…
                </>
              ) : (
                'Generate Report'
              )}
            </button>
            {running && (
              <>
                <p className={styles.statusInfo}>{runningStatusMessage}</p>
                <div className={styles.okayRow}>
                  <button type="button" className={styles.okayBtn} onClick={onClose}>Okay</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySuggestionsPopup;
